import axios from 'axios';
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { getColorByIndex } from 'utils/StyleUtils';

const BASKET_STORAGE_KEY = 'basket';
const LAST_ORDER_STORAGE_KEY = 'lastOrderDate';
const SINGLE_ORDER_COOKIE_KEY = 'shopLastOrder';
const BASKET_ORDER_COOLDOWN = 10 * 60 * 1000;
const SINGLE_ORDER_COOLDOWN = 60 * 1000;

export const ShopContext = createContext(null);
export const useShop = () => useContext(ShopContext);

const readStoredBasket = () => {
    try {
        const storedValue = JSON.parse(localStorage.getItem(BASKET_STORAGE_KEY));
        return Array.isArray(storedValue) ? storedValue : [];
    } catch {
        return [];
    }
};

export default function ShopProvider({ children }) {
    const [shopItems, setShopItems] = useState(null);
    const [shopError, setShopError] = useState(null);
    const [basket, setBasket] = useState(readStoredBasket);

    useEffect(() => {
        let active = true;

        axios
            .post(global.backingsEndpoint + 'productControl/getList', {
                firstResult: 0,
                maxResults: 100,
            })
            .then((response) => {
                if (!active) return;
                setShopItems(Array.isArray(response.data?.data) ? response.data.data : []);
            })
            .catch((error) => {
                if (!active) return;
                console.error('Shop items loading failed:', error);
                setShopError('A termékeket jelenleg nem sikerült betölteni.');
                setShopItems([]);
            });

        return () => {
            active = false;
        };
    }, []);

    useEffect(() => {
        try {
            localStorage.setItem(BASKET_STORAGE_KEY, JSON.stringify(basket));
        } catch (error) {
            console.error('Failed to persist basket:', error);
        }
    }, [basket]);

    const getShopItemById = (id) => shopItems?.find((item) => item.id === id) || null;
    const isInBasket = (id) => basket.includes(id);

    const addToBasket = (id) => {
        setBasket((current) => current.includes(id) ? current : [...current, id]);
    };

    const removeFromBasket = (id) => {
        setBasket((current) => current.filter((itemId) => itemId !== id));
    };

    const clearBasket = () => {
        setBasket([]);

        try {
            localStorage.setItem(BASKET_STORAGE_KEY, JSON.stringify([]));
        } catch (error) {
            console.error('Failed to clear persisted basket:', error);
        }
    };

    const basketItems = useMemo(
        () => basket.map((id) => shopItems?.find((item) => item.id === id)).filter(Boolean),
        [basket, shopItems]
    );

    const basketPointTotal = useMemo(
        () => basketItems.reduce((total, item) => total + Number(item.pointsPrice || 0), 0),
        [basketItems]
    );

    const storeLastOrderDate = (date = new Date()) => {
        localStorage.setItem(LAST_ORDER_STORAGE_KEY, date.toString());
    };

    const getLastOrderDate = useCallback((callback) => {
        const storedValue = localStorage.getItem(LAST_ORDER_STORAGE_KEY);
        const date = storedValue ? new Date(storedValue) : null;
        const validDate = date && Number.isFinite(date.getTime()) ? date : null;
        callback?.(validDate);
        return validDate;
    }, []);

    const deleteLastOrderDate = () => localStorage.removeItem(LAST_ORDER_STORAGE_KEY);

    const isBasketOrderCooldownActive = useCallback(() => {
        const lastOrderDate = getLastOrderDate();
        return !!lastOrderDate && Date.now() - lastOrderDate.getTime() < BASKET_ORDER_COOLDOWN;
    }, [getLastOrderDate]);

    const order = async (token, address) => {
        if (!token) {
            const error = new Error('A kosaras rendeléshez hiányzik a getLoggedInClientDto userToken értéke.');
            error.code = 'SHOP_ORDER_TOKEN_REQUIRED';
            throw error;
        }

        // A mobilappal azonos tokenes rendelési végpont. A token a weben a
        // getLoggedInClientDto válasz userToken mezőjéből érkezik.
        const response = await axios.post(
            global.backingsEndpoint + 'ordersControl/storeWithTokenAndAddress',
            {
                token,
                productList: basket,
                shippingAddress: address,
            }
        );

        // A mobilapphoz igazodva minden sikeresen lefutott HTTP-kérés leadott
        // rendelésnek számít; a végpont válasza lehet falsy érték is.
        storeLastOrderDate();

        return response.data;
    };

    const readCookie = (name) => {
        const cookie = document.cookie.split('; ').find((row) => row.startsWith(`${name}=`));
        return cookie ? decodeURIComponent(cookie.split('=')[1]) : null;
    };

    const writeCookie = (name, value, maxAgeSeconds) => {
        document.cookie = `${name}=${encodeURIComponent(value)}; path=/; max-age=${maxAgeSeconds}`;
    };

    const readSingleOrder = () => {
        try {
            const value = readCookie(SINGLE_ORDER_COOKIE_KEY);
            return value ? JSON.parse(value) : null;
        } catch {
            return null;
        }
    };

    const storeProductOrder = async (productId, userCredit, productPrice) => {
        if (Number(userCredit) < Number(productPrice)) return 'INSUFFICIENT_POINTS';

        const lastOrder = readSingleOrder();
        if (lastOrder?.timestamp && Date.now() < Number(lastOrder.timestamp) + SINGLE_ORDER_COOLDOWN) {
            return false;
        }

        const response = await axios.post(global.backingsEndpoint + 'ordersControl/store', { productId });

        if (response.data !== false) {
            writeCookie(
                SINGLE_ORDER_COOKIE_KEY,
                JSON.stringify({ id: productId, timestamp: Date.now() }),
                SINGLE_ORDER_COOLDOWN / 1000
            );
        }

        return response.data;
    };

    const getShopItemColorByIdAndName = (id, name = '') => {
        let color = getColorByIndex(id);
        const normalizedName = name.toLowerCase();

        if (normalizedName.includes('kék')) color = 'blue';
        if (normalizedName.includes('piros')) color = 'red';
        if (normalizedName.includes('sárga') || normalizedName.includes('narancs')) color = 'yellow';
        if (normalizedName.includes('zöld')) color = 'green';

        return color;
    };

    const value = {
        shopItems,
        shopError,
        basket,
        basketItems,
        basketPointTotal,
        getShopItemById,
        addToBasket,
        removeFromBasket,
        clearBasket,
        isInBasket,
        order,
        storeProductOrder,
        getShopItemColorByIdAndName,
        getLastOrderDate,
        deleteLastOrderDate,
        isBasketOrderCooldownActive,
    };

    return <ShopContext.Provider value={value}>{children}</ShopContext.Provider>;
}
